// 叫貨 demand grouping — extracted from ReceivingQueuePage so that Magnus's
// 2026-09-15 rule 「don't merge」 is enforced by the SHAPE of the data rather than
// by a branch inside a 4,500-line component that a later edit can quietly delete.
//
// The rule: 門市 (counter) demand and customer demand for the SAME SKU and the SAME
// colour/size are two different things. Summing them produces a total that looks
// right while the attribution is gone — and POST /api/receiving/cart-marks/assign
// packs Sinsang accounts in the order this list arrives, so a merged quantity also
// silently reorders which login owns which 款.
//
// Three structural locks, none of which is an `if`:
//   1. `demandGroupKey()` is the only way to spell a group key, and it always folds
//      the demand source in. There is no key for "SKU 3346" — only for
//      "customer × 3346" and "counter × 3346".
//   2. `DemandGroup.demandSource` is required, so a group cannot be constructed
//      without declaring which side it is.
//   3. The Sinsang packer takes both sources but emits each SKU once, so a 款 with a
//      customer card and a 門市 card claims one cart slot, never two.

import { CategoryKey } from '../../utils/productCategory';

/** Which side of the counter the demand came from. Customer orders, or the 門市 rail. */
export type DemandSource = 'customer' | 'counter';

declare const demandGroupKeyBrand: unique symbol;
/** Opaque. Only `demandGroupKey()` produces one, and it never omits the source. */
export type DemandGroupKey = string & { readonly [demandGroupKeyBrand]: true };

// NUL separates the two halves because neither a SKU nor a product name can contain
// it — so "counter" + "3346" can never spell the same string as "customer" + something.
export const demandGroupKey = (source: DemandSource, styleKey: string): DemandGroupKey =>
  `${source} ${styleKey}` as DemandGroupKey;

/** Read the source back off a key, for assertions and for grouping the rendered list. */
export const demandSourceOfKey = (key: DemandGroupKey): DemandSource =>
  (key.split(' ')[0] as DemandSource);

/** Read the style key (SKU, or product name when there is no SKU) back off a key. */
export const styleKeyOfKey = (key: DemandGroupKey): string =>
  key.slice(key.indexOf(' ') + 1);

export interface DemandSpec {
  variant: string;
  totalQty: number;
  orders: string[];
}

/** One 款 of ONE demand source: the product plus every pending colour/size under it. */
export interface DemandGroup {
  productName: string;
  sku: string;
  imageUrl: string;
  specs: DemandSpec[];
  totalQty: number;
  orderCount: number;
  key: DemandGroupKey;
  demandSource: DemandSource;
}

export type CustomerDemandGroup = DemandGroup & { demandSource: 'customer' };
export type CounterDemandGroup = DemandGroup & { demandSource: 'counter' };

export const isCustomerDemand = (g: DemandGroup): g is CustomerDemandGroup => g.demandSource === 'customer';
export const isCounterDemand = (g: DemandGroup): g is CounterDemandGroup => g.demandSource === 'counter';

/**
 * The only door to `CustomerDemandGroup[]`. Anything that must not see 門市 demand
 * takes that type and therefore has to come through here.
 */
export function partitionDemand(groups: DemandGroup[]): {
  customer: CustomerDemandGroup[];
  counter: CounterDemandGroup[];
} {
  const customer: CustomerDemandGroup[] = [];
  const counter: CounterDemandGroup[] = [];
  for (const g of groups) {
    if (isCustomerDemand(g)) customer.push(g);
    else if (isCounterDemand(g)) counter.push(g);
  }
  return { customer, counter };
}

// ── input shapes (structural, so UnifiedOrder/UnifiedOrderItem satisfy them) ──

export interface DemandInputItem {
  sku: string;
  productName: string;
  variantOrSpec: string;
  quantity: number;
  counterLine?: { remainingQty?: number };
  received: boolean;
  /** Out of stock at the supplier: nothing to buy, so the line never reaches the Sinsang cart. */
  outOfStock?: boolean;
  /** Shopee: size changed in the OMS; the buyer's order still says variantOrSpec. Buy this one. */
  specOverride?: string | null;
  imageUrl: string;
}

export interface DemandInputOrder {
  orderNumber: string;
  platform: string;
  orderDateDisplay: string;
  items: DemandInputItem[];
  /** Absent means a customer order — every order that existed before 門市 orders did. */
  demandSource?: DemandSource;
}

export interface BuildDemandOptions {
  /** Platform chip. 'all' keeps everything. */
  sourceFilter: string;
  /** `YYYY-MM-DD HH:mm` wall-clock lower bound, '' for no bound. */
  afterStr: string;
  categoryFilter: CategoryKey | '';
  classify: (productName: string) => CategoryKey;
}

/** Normalize a variant: "海軍藍,M" and "海軍藍 / M" both become "海軍藍 / M". */
export const normalizeVariant = (raw: string): string =>
  (raw || '-').replace(/\s*[,，]\s*/g, ' / ').replace(/\s*\/\s*/g, ' / ').trim() || '-';

// A plain object key is an array index when it is the canonical decimal spelling of an
// integer in [0, 2^32-2]. Almost every Metanoia SKU ("3346") is one.
const ARRAY_INDEX = /^(0|[1-9][0-9]*)$/;
const isArrayIndexKey = (k: string) => ARRAY_INDEX.test(k) && Number(k) <= 4294967294;

const SIZE_ORDER: Record<string, number> = { XS: 1, S: 2, M: 3, L: 4, XL: 5, '2XL': 6, '3XL': 7 };

/**
 * Group unreceived lines into one card per 款 PER DEMAND SOURCE.
 *
 * Output order is deliberately identical to the old in-component version for a
 * customer-only input: the old code kept groups in a plain object keyed by a bare
 * SKU, so V8 handed back the array-index-like keys numerically ascending and the
 * rest in insertion order — and because the totalQty sort below is stable, that
 * ordering still decides every tie. Ties decide which Sinsang cart account a 款
 * lands in, so it is reproduced here instead of being quietly changed by the move
 * to a Map.
 */
export function buildDemandGroups(
  orders: DemandInputOrder[],
  opts: BuildDemandOptions,
): { list: DemandGroup[]; counts: Map<CategoryKey, number> } {
  const counts = new Map<CategoryKey, number>();
  const groups = new Map<DemandGroupKey, DemandGroup>();
  // Parallel bookkeeping for the ordering emulation above — never part of the output.
  const styleKeyOf = new Map<DemandGroupKey, string>();
  const insertionOf = new Map<DemandGroupKey, number>();

  orders.forEach(order => {
    if (opts.sourceFilter !== 'all' && order.platform !== opts.sourceFilter) return;
    if (opts.afterStr && order.orderDateDisplay.slice(0, 16) < opts.afterStr) return;
    const demandSource: DemandSource = order.demandSource || 'customer';
    order.items.forEach(item => {
      if (item.received || item.outOfStock) return;
      const quantity = demandSource === 'counter' ? (item.counterLine?.remainingQty ?? item.quantity) : item.quantity;
      if (quantity <= 0) return;
      const cat = opts.classify(item.productName);
      counts.set(cat, (counts.get(cat) || 0) + 1);
      if (opts.categoryFilter && cat !== opts.categoryFilter) return;
      const styleKey = item.sku || item.productName;
      if (!styleKey) return;
      const key = demandGroupKey(demandSource, styleKey);
      let group = groups.get(key);
      if (!group) {
        group = {
          productName: item.productName, sku: item.sku, imageUrl: item.imageUrl,
          specs: [], totalQty: 0, orderCount: 0, key, demandSource,
        };
        groups.set(key, group);
        styleKeyOf.set(key, styleKey);
        insertionOf.set(key, insertionOf.size);
      }
      const variant = normalizeVariant(item.specOverride || item.variantOrSpec);
      let spec = group.specs.find(s => s.variant === variant);
      if (!spec) { spec = { variant, totalQty: 0, orders: [] }; group.specs.push(spec); }
      spec.totalQty += quantity;
      if (!spec.orders.includes(order.orderNumber)) spec.orders.push(order.orderNumber);
      group.totalQty += quantity;
      if (!group.imageUrl && item.imageUrl) group.imageUrl = item.imageUrl;
    });
  });

  const result = Array.from(groups.values());
  result.sort((a, b) => {
    const ak = styleKeyOf.get(a.key) as string;
    const bk = styleKeyOf.get(b.key) as string;
    const ai = isArrayIndexKey(ak);
    const bi = isArrayIndexKey(bk);
    if (ai !== bi) return ai ? -1 : 1;
    if (ai && bi && ak !== bk) return Number(ak) - Number(bk);
    return (insertionOf.get(a.key) as number) - (insertionOf.get(b.key) as number);
  });

  result.forEach(g => {
    const orderSet = new Set<string>();
    g.specs.forEach(s => s.orders.forEach(o => orderSet.add(o)));
    g.orderCount = orderSet.size;
    g.specs.sort((a, b) => {
      // Variant format: "color / size" (e.g. "白色 / L"). Sort color first, then size.
      const [aColor = '', aSize = ''] = a.variant.split(' / ');
      const [bColor = '', bSize = ''] = b.variant.split(' / ');
      const colorCmp = aColor.localeCompare(bColor, 'zh-TW');
      if (colorCmp !== 0) return colorCmp;
      const aRank = SIZE_ORDER[aSize.toUpperCase()] || 500;
      const bRank = SIZE_ORDER[bSize.toUpperCase()] || 500;
      return aRank - bRank || aSize.localeCompare(bSize, 'zh-TW');
    });
  });
  result.sort((a, b) => b.totalQty - a.totalQty);
  return { list: result, counts };
}

/**
 * The SKU list POSTed to /api/receiving/cart-marks/assign, in priority order.
 *
 * Takes BOTH demand sources: a Sinsang cart holds a 款, and a 門市 款 is bought from
 * the same page as a customer 款 (Magnus 2026-10-21: 「門市 should just be treated as
 * same product」). Cards stay separate (「don't merge」 2026-09-15) — only the cart
 * slot is shared, so a SKU with both a customer and a 門市 card appears once.
 *
 * ORDER IS LOAD-BEARING: the server packs 帳號1..N in the order this array arrives.
 * First appearance wins, and the list arrives sorted by each card's own (unmerged)
 * totalQty, so a 款's slot is ranked by its biggest single card.
 */
export function cartEligibleSkus(
  groups: DemandGroup[],
  hasSourcingLink: (sku: string) => boolean,
): string[] {
  const seen = new Set<string>();
  const skus: string[] = [];
  for (const g of groups) {
    if (!g.sku || seen.has(g.sku) || !hasSourcingLink(g.sku)) continue;
    seen.add(g.sku);
    skus.push(g.sku);
  }
  return skus;
}
